import { Injectable, Inject } from '@angular/core';
import { StompConfig, StompService } from '@stomp/ng2-stompjs';
import { Subject } from 'rxjs';
import { max, min, random, round } from 'lodash';
import {
  BenchmarkService,
  IChartData,
  UserService,
  NotificationService,
  AutoRefreshService
} from '@nrwl/common-lib';
import { Query } from '../data/query';
import { TimelineCountDataGenerationService } from './timeline-count-data-generation.service';
import { QueryEmitterProvider, SubscriptionRules } from './query.emitter.provider';
import { IAnomaly, IAnomalyDrillDownSeries, IWidgetQuery } from './types';

const m1 = require('./mocks/flow.bitspersecond.json');
const m2 = require('./mocks/flow.bitspersecond.json');
const geojson = require('./mocks/geo.json');
const topAnomalies = require('./mocks/top_anomalies.json');

/**
 * Important note: those widgets that want to use mocked/not real responses, must
 * use private provider and call 'bindMocks' before the mock provider can be used.
 */
@Injectable()
export class QueryEmitterMocksProvider extends QueryEmitterProvider {
  mockId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mockData: any;

  constructor(
    private timelineCountDataGenerationService: TimelineCountDataGenerationService,
    _stompService: StompService,
    userService: UserService,
    @Inject('defaultSocketsConfig') stompConfig: StompConfig,
    benchmarkService: BenchmarkService,
    notificationService: NotificationService,
    autoRefreshService: AutoRefreshService
  ) {
    super(
      _stompService,
      userService,
      stompConfig,
      benchmarkService,
      notificationService,
      autoRefreshService
    );
  }

  bindMocks(uuid: string, mockId: string): void {
    this.mockId = mockId;
    // asking to bind specific mock destroys any existing registrations on stomp and replaces it with dummy Subject
    if (this.widgets.get(uuid)) {
      this.onDestroy(uuid);
    }
    this.registerWidget(uuid, true);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sendQuery(subscribeUrl: SubscriptionRules, register: IWidgetQuery, query: Query): void {
    switch (this.mockId) {
      case 'm1':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(m1);
        break;
      case 'm2':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(m2);
        break;
      case 'anomalies':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(this.getAnomalies());
        break;
      case 'geo-anomalies':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(geojson);
        break;
      case '/anomalywatcher/v1/queryanomaly':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(topAnomalies);
        break;
      case '/anomalywatcher/v1/queryanomaly/timelineCount':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(
          this.timelineCountDataGenerationService.generate(this.mockData.filter, this.mockData)
        );
        break;
      case '/anomalywatcher/v1/queryAnomalyDrillDown':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(
          this.getAnomalyDrillDownSeries(Date.now(), 1000, [
            'eueclientscore',
            'eueserverscore',
            'euenetworkscore',
            'eueapplicationscore'
          ])
        );
        break;
      case '/anomalywatcher/v1/queryAnomalyDrillDown/mosVoipCallQuality':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(
          this.getAnomalyDrillDownSeries(Date.now(), 1000, ['mosaudioaverage'])
        );
        break;
      case '/anomalywatcher/v1/queryAnomalyDrillDown/singleValueChartVoipCallQuality':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (register.dataSource as Subject<any>).next(
          this.getSingleValueChartData('lossrateaverage', 1000)
        );
        break;
      default:
        console.error('Unknown mockID:', this.mockId);
    }
  }

  private getAnomalyDrillDownSeriesChunk(
    now: number,
    count: number,
    kpiName: string
  ): IAnomalyDrillDownSeries {
    const aggregationRange = 60000;
    let timestamp = now - count * aggregationRange;
    const result = {
      kpiName,
      timeSeriesData: []
    };

    const warningThreshold = 5,
      criticalThreshold = 3;

    for (let i = 0; i < count; i++) {
      const prev = i > 0 ? result.timeSeriesData[i - 1].measuredValue : random(0, 10, true);
      const next = min([max([prev + random(-0.25, 0.25, true), 0]), 10]);
      timestamp += aggregationRange;
      result.timeSeriesData.push({
        timestamp,
        measuredValue: next,

        upperLimit: 0,
        lowerLimit: 0,
        warningThreshold,
        criticalThreshold,
        severity:
          next <= criticalThreshold ? 'critical' : next <= warningThreshold ? 'warning' : 'good'
      });
    }

    return result;
  }

  private getSingleValueChartData(dataType: string, count: number): IChartData[] {
    const now = Date.now();
    const aggregationRange = 60000;
    let timestamp = now - count * aggregationRange;
    const results = [];

    for (let i = 0; i < count; i++) {
      const prev = i > 0 ? results[i - 1][dataType] : random(0, 10, true);
      const next = min([max([prev + random(-0.25, 0.25, true), 0]), 10]);
      timestamp += aggregationRange;
      results.push({
        timestamp,
        [dataType]: next
      });
    }

    return results;
  }

  private getAnomalyDrillDownSeries(
    now: number,
    count: number,
    kpiNames: Array<string>
  ): IAnomalyDrillDownSeries[] {
    return kpiNames.map(kpiName => {
      return this.getAnomalyDrillDownSeriesChunk(now, count, kpiName);
    });
  }

  private getAnomalies(): IAnomaly[] {
    const dataSource = [];
    for (let i = 0; i < 10; i++) {
      dataSource.push({
        title: 'APPLICATION ANOMALY ' + i,
        description: 'Gemalto performance degradation in All Sites ' + i,
        date: new Date().getTime() - 60000 * random(10),
        score: round(random(10, true), 1),
        observations: random(99),
        convCount: random(300),
        duration: random(600),
        affected: random(10)
      });
    }
    return dataSource;
  }
}
